/**
 * Каталог приводов (E14 / ФТ-12.1–12.3).
 * Правило бренда по умолчанию — AN-027 до закрытия Q6 Заказчиком.
 */

import { z } from "zod";
import { decimalStringSchema } from "./catalog";
import { apiSuccessSchema } from "./envelope";

export const driveCatalogItemSchema = z.object({
  id: z.string().uuid(),
  brand: z.string().trim().min(1).max(128),
  designation: z.string().trim().min(1).max(255),
  powerKw: decimalStringSchema,
  rpm: decimalStringSchema.nullable(),
  torqueNm: decimalStringSchema.nullable(),
  brakeType: z.string().trim().min(1).max(128).nullable(),
  preferredForGroupId: z.string().uuid().nullable(),
});
export type DriveCatalogItem = z.infer<typeof driveCatalogItemSchema>;

export const driveCatalogListResponseSchema = apiSuccessSchema(z.array(driveCatalogItemSchema));
export const driveCatalogItemResponseSchema = apiSuccessSchema(driveCatalogItemSchema);

export const createDriveCatalogItemRequestSchema = z.object({
  brand: z.string().trim().min(1).max(128),
  designation: z.string().trim().min(1).max(255),
  powerKw: decimalStringSchema.refine((v) => Number(v) > 0, "Мощность должна быть > 0"),
  rpm: decimalStringSchema.nullable().optional(),
  torqueNm: decimalStringSchema.nullable().optional(),
  brakeType: z.string().trim().min(1).max(128).nullable().optional(),
  preferredForGroupId: z.string().uuid().nullable().optional(),
});
export type CreateDriveCatalogItemRequest = z.infer<typeof createDriveCatalogItemRequestSchema>;

export const updateDriveCatalogItemRequestSchema = createDriveCatalogItemRequestSchema;
export type UpdateDriveCatalogItemRequest = z.infer<typeof updateDriveCatalogItemRequestSchema>;

export const selectDriveRequestSchema = z.object({
  requiredPowerKw: decimalStringSchema.refine((v) => Number(v) > 0, "Мощность должна быть > 0"),
  groupId: z.string().uuid().nullable().optional(),
});
export type SelectDriveRequest = z.infer<typeof selectDriveRequestSchema>;

export const selectDriveResultSchema = z.object({
  drive: driveCatalogItemSchema.nullable(),
});
export type SelectDriveResult = z.infer<typeof selectDriveResultSchema>;

export const selectDriveResultResponseSchema = apiSuccessSchema(selectDriveResultSchema);

export const DRIVE_CATALOG_ERROR_CODES = {
  NOT_FOUND: "DRIVE_CATALOG_NOT_FOUND",
  GROUP_NOT_FOUND: "DRIVE_CATALOG_GROUP_NOT_FOUND",
} as const;
export type DriveCatalogErrorCode =
  (typeof DRIVE_CATALOG_ERROR_CODES)[keyof typeof DRIVE_CATALOG_ERROR_CODES];

/**
 * Подбор минимально подходящего привода (ФТ-12.2–12.3 / AN-027).
 * 1) Кандидаты: powerKw >= required.
 * 2) Если для groupId есть предпочтительные — только они; иначе все кандидаты.
 * 3) Минимальная мощность; при равенстве — designation ASC.
 */
export function selectMinimalDrive(
  drives: readonly DriveCatalogItem[],
  requiredPowerKw: string,
  groupId: string | null = null,
): DriveCatalogItem | null {
  const required = Number(requiredPowerKw);
  if (!Number.isFinite(required) || required <= 0) {
    return null;
  }

  const eligible = drives.filter((row) => Number(row.powerKw) >= required);
  if (eligible.length === 0) {
    return null;
  }

  const preferred =
    groupId === null
      ? []
      : eligible.filter((row) => row.preferredForGroupId === groupId);
  const pool = preferred.length > 0 ? preferred : eligible;

  return [...pool].sort((left, right) => {
    const powerDiff = Number(left.powerKw) - Number(right.powerKw);
    if (powerDiff !== 0) {
      return powerDiff;
    }
    return left.designation.localeCompare(right.designation, "ru");
  })[0]!;
}
